<template>
  <div id="users h-full">
    <div id="users_topBar" class="w-9/10 mx-auto px-1 text-left">
      <input v-model="searchInput" type="text" :placeholder="getTranslation('search')"
             class="border-shadow rounded-20 search mx-auto mt-2 mb-2 pl-2 search mr-1">
      <c-option-dropdown :options="options" :selectedOption.sync="selectedOption" :rounded="true"/>
      <button @click="reset()"
              class="classen__button reset classen_rounded_full classen__button_active ml-1 mt-2">
        <p class="leading-none">Reset</p>
      </button>
      <button
        @click="showAddUserPopup=true"
        v-if="hasPermissionToAddUsers"
        class="classen__button classen__button_active ml-1 mt-2 px-3 float-right">
        <p class="leading-none">+ {{ getTranslation('addUser') }}</p>
      </button>
    </div>
    <div id="users_table_div" class="w-full h-9/10 text-left w-9/10 mx-auto">
      <div
        class="grid grid-cols-smallUserTable 1.5xl:grid-cols-userTable font-bold h-1/20 border-b-2 border-classen__secondary ">
        <p class="cursor-pointer self-end" @click="sortByColumn('name')">
          {{ getTranslation('name') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="cursor-pointer self-end" @click="sortByColumn('email')">
          {{ getTranslation('email') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="cursor-pointer self-end" @click="sortByColumn('role')">
          {{ getTranslation('role') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="cursor-pointer self-end" @click="sortByColumn('language')">
          {{ getTranslation('language') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="cursor-pointer self-end" @click="sortByColumn('isActive')">
          {{ getTranslation('isActive') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="cursor-pointer self-end hidden 1.5xl:block" @click="sortByColumn('lastActive')">
          {{ getTranslation('lastActive') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p v-if="isMasterAdmin" class="cursor-pointer self-end hidden 1.5xl:block"
           @click="sortByColumn('loggedInCount')">
          {{ getTranslation('registrations') }}
          <font-awesome-icon icon="sort"></font-awesome-icon>
        </p>
        <p class="self-end text-right">{{ getTranslation('actions') }}</p>
      </div>
      <div class="h-19/20 overflow-y-auto">
        <div v-for="user in paginatedUsers"
             class="grid grid-cols-smallUserTable 1.5xl:grid-cols-userTable user">
          <p class="break-words w-9/10">{{ user.name }}</p>
          <p class="overflow-ellipsis overflow-hidden w-9/10">{{ user.email }}</p>
          <p>{{ getUserRole(user.permissions.role) }}</p>
          <p>{{ getLanguage(user.languageId) }}</p>
          <p>{{ user.isActive ? getTranslation('yes') : getTranslation('no') }}</p>
          <p class="hidden 1.5xl:block">{{
              toDate(user.loggedInAt)
            }}</p>
          <p v-if="isMasterAdmin" class="hidden 1.5xl:block">{{ user.loggedInCount }}</p>
          <div class="flex justify-end text-center items-center justify-items-end text-white">
            <div v-if="hasPermissionToEditUsers && isLowerInRanking(user)"
                 class="action bg-classen__primary col-start-2 flex justify-center items-center cursor-pointer"
                 @click="toggleUserEditPopup(user)">
              <i class="fas fa-pen"></i>
            </div>
            <div v-if="hasPermissionToDeactivateUsers"
                 @click=toggleUserActivationConformation(user)
                 class="ml-2 action bg-classen__primary flex justify-center items-center cursor-pointer">
              <img src="../assets/icons/deactivate_user.svg" class="h-5">
            </div>
            <div v-if="hasPermissionToDeleteUsers" @click="userToDelete = user"
                 class="ml-2 action cursor-pointer bg-classen__primary flex justify-center items-center">
              <i class="fas fa-trash"></i>
            </div>
          </div>
        </div>
        <div class="load-more flex justify-center">
          <button v-if="hasMore" class="classen__button hover__button "
                  @click="page++">
            <p class="leading-none">{{ getTranslation('loadMoreItems') }}</p>
          </button>
        </div>
      </div>
    </div>

    <c-add-user-popup v-if="showAddUserPopup" @cancel="showAddUserPopup=false"/>
    <c-edit-user-popup v-if="showEditUserPopup" :user="selectedUser"
                       @cancel="showEditUserPopup=false"/>
    <c-confirm v-if="showUserDisablePopup"
               :cancel-text="getTranslation('cancel')"
               :confirm-text="getTranslation('yesConfirm')"
               :phrase="getTranslation(confirmationPhrase)"
               @cancel="showUserDisablePopup = false"
               @confirm="toggleUserActivation"
    />
    <c-delete-user-popup v-if="userToDelete" @cancel="userToDelete=null" :user="userToDelete"/>
  </div>
</template>

<script>
import getTranslation from "../mixins/getTranslation";
import dateConversion from "../mixins/dateConversion";
import OptionDropdown from "../components/structure/OptionDropdown";
import AddUserPopup from "./AddUserPopup";
import EditUserPopup from "./EditUserPopup";
import ConfirmationPopup from "../components/form/ConfirmationPopup";
import getPermissions from "../mixins/getPermissions";
import DeleteUserPopup from "../components/structure/Users/DeleteUserPopup";

export default {
  name: 'Users',

  mixins: [getTranslation, dateConversion, getPermissions],

  components: {
    'c-option-dropdown': OptionDropdown,
    'c-add-user-popup': AddUserPopup,
    'c-delete-user-popup': DeleteUserPopup,
    'c-edit-user-popup': EditUserPopup,
    'c-confirm': ConfirmationPopup
  },

  data() {
    return {
      page: 1,
      size: 25,
      userToDelete: null,
      searchInput: '',
      selectedOption: {value: "", name: 'None selected'},
      options: [
        {value: "", name: "None selected"},
        {value: "masteradmin", name: "Master Admin"},
        {value: "admin", name: "Admin"},
        {value: "regionalproductadmin", name: "Regional Admin"},
        {value: "salesadmin", name: "Sales Admin"},
        {value: "production", name: "Production"},
        {value: "flexible", name: "Flexible"}
      ],

      showAddUserPopup: false,
      showEditUserPopup: false,
      showUserDisablePopup: false,

      sortBy: {
        name: true,
        email: true,
        role: true,
        language: true,
        isActive: true,
        lastActive: true,
        loggedInCount: true,
      },

      confirmationPhrase: '',
      selectedUser: null
    }
  },

  created() {
    document.title = 'The Qube - Users';
  },

  computed: {
    hasMore() {
      return (this.page * this.size) <= this.filteredUsers.length
    },

    canSeeEditViewButton() {
      return this.canViewUser || this.canEditUser
    },

    activeUser() {
      return this.$dashboardStore.getters.getActiveUser();
    },

    languages() {
      return this.$dashboardStore.getters.getLanguages;
    },

    users() {
      let users = this.$dashboardStore.getters.getUsers;
      if (this.activeUserHasRole(this.ROLES.REGIONAL_ADMIN)) {
        users = users.filter((user) => {
          return user.regions.length !== 0
            && user.regions.some((region) => this.activeUser.regions.includes(region));
        });
      }
      return users;
    },

    filteredUsers() {
      if (this.users.length === 0) {
        return [];
      }

      this.page = 1;
      let users = [];
      users = this.users.filter((user) => {
        if (!user.name || !user.email) {
          return true;
        }

        return user.name.toLowerCase().includes(this.searchInput.toLowerCase()) || user.email.toLowerCase().includes(this.searchInput.toLowerCase());
      });

      if (this.selectedOption.value === '') {
        return users;
      }

      return users.filter((user) => {
        return user.permissions.role === this.selectedOption.value;
      })
    },

    paginatedUsers() {
      let numberOfUsersToDisplay = this.page * this.size;
      return this.filteredUsers.slice(1, numberOfUsersToDisplay)
    },

    isMasterAdmin() {
      return this.activeUserHasRole(this.ROLES.MASTER_ADMIN);
    }
  },

  methods: {
    getLanguage(id) {
      return this.languages.find((lan) => {
        return lan.id === id
      }).name
    },

    reset() {
      this.selectedOption = {value: "", name: 'None selected'};
      this.searchInput = ''
    },

    toggleUserActivation() {
      this.selectedUser.isActive = !this.selectedUser.isActive;

      this.$dashboardStore.dispatch('updateUser', {
        userId: this.selectedUser.id,
        userData: {
          id: this.selectedUser.id,
          name: this.selectedUser.name,
          email: this.selectedUser.email,
          languageId: this.selectedUser.languageId,
          permissions: this.selectedUser.permissions,
          isActive: this.selectedUser.isActive,
          regions: this.selectedUser.regions.map((x) => x.id)
        }
      })
        .then(() => {
          this.showUserDisablePopup = false;
          this.selectedUser = null;
        });
    },

    toggleUserActivationConformation(user) {
      this.selectedUser = user;

      this.confirmationPhrase = this.selectedUser.isActive ? 'confirmUserDeactivate' : 'confirmUserActivate';
      this.showUserDisablePopup = true;
    },

    toggleUserEditPopup(user) {
      this.selectedUser = user;

      this.showEditUserPopup = true;
    },

    sortByColumn(column) {
      if (column === 'name' || column === 'email') {
        this.sortAlphabetical(column);
        return;
      }

      if (column === 'role') {
        this.sortByRole(column);
        return;
      }

      if (column === 'language') {
        this.sortByLanguage(column);
        return;
      }

      if (column === 'isActive') {
        this.sortByIsActive(column);
        return;
      }

      if (column === 'lastActive') {
        this.sortByLastActive();
        return;
      }

      if (column === 'loggedInCount') {
        this.sortByRegistrations(column);
      }
    },

    sortAlphabetical(column) {
      this.users = this.users.sort((a, b) => a[column].localeCompare(b[column]));
      this.reverseArrayIfNecessary(column);
    },

    sortByRole(column) {
      this.users = this.users.sort((a, b) => a.permissions.role.localeCompare(b.permissions.role));
      this.reverseArrayIfNecessary(column);
    },

    sortByLanguage(column) {
      this.users = this.users.sort((a, b) => {
        let languageNameA = this.getLanguage(a.languageId);
        let languageNameB = this.getLanguage(b.languageId);
        return languageNameA.localeCompare(languageNameB);
      });
      this.reverseArrayIfNecessary(column);
    },

    sortByIsActive(column) {
      this.users = this.users.sort((a, b) => a.isActive - b.isActive);
      this.reverseArrayIfNecessary(column);
    },

    sortByRegistrations(column) {
      this.users = this.users.sort((a, b) => a.loggedInCount - b.loggedInCount);
      this.reverseArrayIfNecessary(column);
    },

    reverseArrayIfNecessary(column) {
      if (!this.sortBy[column]) {
        this.users = this.users.reverse();
      }
      this.sortBy[column] = !this.sortBy[column];
    },

    sortByLastActive() {
      this.users = this.users.sort((a, b) => {
        if (a.loggedInAt !== null && b.loggedInAt === null) {
          return -1;
        }

        if (a.loggedInAt === null && b.loggedInAt !== null) {
          return 1;
        }

        if (a.loggedInAt !== null && b.loggedInAt !== null) {
          if (this.sortBy.lastActive) {
            return new Date(b.loggedInAt) - new Date(a.loggedInAt);
          }
          return new Date(a.loggedInAt) - new Date(b.loggedInAt);
        }

        return 0;
      })

      this.sortBy.lastActive = !this.sortBy.lastActive;
    },

    isLowerInRanking(user) {
      const role = this.ROLE_MAP[user.permissions.role]
      const userRanking = this.ROLES[role].ranking;
      const activeUserRole = this.ROLE_MAP[this.activeUser.permissions.role];
      const activeUserRanking = this.ROLES[activeUserRole].ranking;
      return userRanking >= activeUserRanking
    },

    getUserRole(roleName) {
      const role = this.options.find((option) => option.value === roleName);
      return role ? role.name : '';
    }
  }

};
</script>

<style scoped>
.action {
  height: 40px;
  width: 40px;
  border-radius: 40px;
}

.user {
  padding-top: 15px;
  padding-bottom: 15px;
  height: 70px;
}

.search, .reset {
  height: 35px
}

.classen__button {
  height: 35px;
  min-width: 150px;
  width: auto;
}

.load-more {
  grid-column: 1/-1;
}
</style>
